'use client'

import React from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { socialLinks } from '@/const'
import { BehanceIcon, DzenIcon, InstagramIcon, TelegramIcon, VkIcon } from '@/components/Icon'
import IconButton from '@/components/IconButton'
import LinkComponent from '@/components/Link'
import { PHONE } from '@/shared/constants'
import styles from './styles.module.css'

interface FooterProps {
	onProjectClick: () => void
	onConsultationClick: () => void
}

const Footer: React.FC<FooterProps> = ({ onProjectClick, onConsultationClick }) => {
	const pathname = usePathname()
	const isHomePage = pathname === '/'

	return (
		<footer className={styles.footer}>
			<div className={styles.container}>
				{/* Left part */}
				<div className={styles.leftSection}>
					{/* Logo */}
					<div className={styles.logo}>
						{isHomePage ? (
							<img src='/logo.svg' alt='true.code' className={styles.logoImage} />
						) : (
							<Link href='/'>
								<img src='/logo.svg' alt='true.code' className={styles.logoImage} />
							</Link>
						)}
					</div>

					{/* Social media icons */}
					<div className={styles.socialIcons}>
						<IconButton Icon={TelegramIcon} variant='circle' size='sm' asChild>
							<LinkComponent href={socialLinks.telegram} target='_blank' rel='noopener noreferrer' />
						</IconButton>
						<IconButton Icon={VkIcon} variant='circle' size='sm' asChild>
							<LinkComponent href={socialLinks.vk} target='_blank' rel='noopener noreferrer' />
						</IconButton>
						<IconButton Icon={BehanceIcon} variant='circle' size='sm' asChild>
							<LinkComponent href={socialLinks.behance} target='_blank' rel='noopener noreferrer' />
						</IconButton>
						<IconButton Icon={DzenIcon} variant='circle' size='sm' asChild>
							<LinkComponent href={socialLinks.dzen} target='_blank' rel='noopener noreferrer' />
						</IconButton>
						<IconButton variant='circle' size='sm' asChild>
							<LinkComponent href={socialLinks.vc} target='_blank' rel='noopener noreferrer'>
								<svg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
									<g clipPath='url(#clip0_2115_2862)'>
										<path
											d='M9.58236 5.57127C10.4603 5.57127 11.1212 5.20746 11.6086 4.57409L10.9572 4.07978C10.7964 4.29069 10.5884 4.46155 10.3498 4.57889C10.1113 4.69624 9.84852 4.75687 9.58236 4.75599C8.62956 4.75599 7.93914 3.98388 7.93914 3.03646C7.93914 2.08903 8.61829 1.27343 9.60953 1.27343C10.0502 1.27343 10.3616 1.3769 10.6332 1.54431V2.37738H11.4529V1.16271L11.2958 1.03978C10.8453 0.687178 10.1572 0.5 9.58203 0.5C8.17601 0.5 7.03238 1.63757 7.03238 3.03613C7.03238 4.43338 8.17568 5.57127 9.58236 5.57127ZM11.2829 10.5737L11.2538 9.40611V6.55692H9.54427V7.37253H10.3904V9.3056C10.3904 10.1383 9.75099 10.7279 9.10596 10.7279C8.46093 10.7279 8.12433 10.283 8.12433 9.4506V6.55659H6.52748V7.3722H7.26097V9.59197C7.26097 10.7216 7.89772 11.5 9.03341 11.5C9.68606 11.5 10.183 11.1398 10.4795 10.5845H10.4928L10.6097 11.3609H11.9233V10.5747H11.2839V10.573L11.2829 10.5737ZM6.84354 0.619625H5.41897L4.17992 4.19841H4.1607L2.92828 0.619625H1.41028V1.43556H2.26403L3.73333 5.51459H4.51387V5.5136L6.04611 1.43589H6.84288V0.619957L6.84354 0.619625ZM0.0771484 11.3715H0.921291V10.5321H0.0774797V11.3715H0.0771484ZM3.3371 7.5027H3.32783L3.27482 6.55692H1.76808V7.30036H2.56982V10.574H1.76808V11.3606H5.49185V10.5895H3.4186V8.6139C3.51236 7.8988 4.05138 7.28817 4.78685 7.28817H4.80011L4.7852 8.01909H5.60482V6.5434H4.95681C4.21471 6.54143 3.66409 6.94083 3.33677 7.50303L3.3371 7.5027Z'
											fill='white'
										/>
									</g>
									<defs>
										<clipPath id='clip0_2115_2862'>
											<rect width='12' height='12' fill='white' />
										</clipPath>
									</defs>
								</svg>
							</LinkComponent>
						</IconButton>
					</div>
				</div>

				{/* Page links block */}
				<div className={styles.linksSection}>
					{/* Services */}
					<div className={styles.servicesColumn}>
						<div className={styles.servicesHeader}>
							<Link href='/services' className={styles.servicesTitle}>
								<span className={styles.titleText}>Услуги</span>
							</Link>

							<div className={styles.servicesList}>
								<Link href='/services/development' className={styles.serviceItem}>
									<span className={styles.serviceText}>Разработка</span>
								</Link>
								<Link href='/services/support' className={styles.serviceItem}>
									<span className={styles.serviceText}>Техническое сопровождение</span>
								</Link>
								<Link href='/services/outstaffing' className={styles.serviceItem}>
									<span className={styles.serviceText}>Аутстаффинг</span>
								</Link>
								<div className={styles.serviceItem} onClick={onConsultationClick}>
									<span className={styles.serviceText}>Технологическая экспертиза</span>
								</div>
								<Link href='/services/fintech' className={styles.serviceItem}>
									<span className={styles.serviceText}>Финтех-решения</span>
								</Link>
								<Link href='/services/integrations' className={styles.serviceItem}>
									<span className={styles.serviceText}>Интеграции</span>
								</Link>
							</div>
						</div>

						{/* Privacy policy link - прижата к низу */}
						<Link href='/privacy.pdf' target='_blank' rel='noopener noreferrer' className={styles.privacyLink}>
							<span className={styles.privacyText}>Политика конфиденциальности</span>
						</Link>
					</div>

					{/* Pages */}
					<div className={styles.pagesColumn}>
						{/* <div className={styles.pageItem}>
              <span className={styles.pageText}>О нас</span>
            </div> */}
						<Link href='/cases' className={styles.pageItem}>
							<span className={styles.pageText}>Кейсы</span>
						</Link>
						<Link href='/articles' className={styles.pageItem}>
							<span className={styles.pageText}>Блог</span>
						</Link>
						<Link href='/contacts' className={styles.pageItem}>
							<span className={styles.pageText}>Контакты</span>
						</Link>
					</div>
				</div>

				{/* Contacts block */}
				<div className={styles.contactsSection}>
					{/* Contacts */}
					<div className={styles.contactsContent}>
						<div className={styles.phoneContainer}>
							<Link href={`tel:${PHONE}`} className={styles.phoneNumber}>
								+7 (3952) 261-260
							</Link>
						</div>

						<button className={styles.projectButton} onClick={onProjectClick}>
							<span className={styles.buttonText}>Обсудить проект</span>
							<svg className={styles.buttonIcon} viewBox='0 0 16 16' fill='none'>
								<path
									d='M6.33333 3V4.33333H10.7267L3 12.06L3.94 13L11.6667 5.27333V9.66667H13V3H6.33333Z'
									fill='white'
								/>
							</svg>
						</button>
						<div className={styles.infoCompany}>
							<span className={styles.infoCompanyText}>ООО &quot;Верный код&quot;</span>
							<span className={styles.infoCompanyText}>ИНН 3811076486</span>
							<span className={styles.infoCompanyText}>ОКВЭД 62.01 (основной)</span>
							<span className={styles.infoCompanyText}>
								Код IT-деятельности №1.01 по перечню из Приказа Минцифры № 449
							</span>
						</div>
					</div>

					{/* Legal details */}
					<div className={styles.legalContainer}>
						<div className={styles.copyright}>© {new Date().getFullYear()}, true.code</div>
					</div>
				</div>

				{/* Mobile-only elements for correct order */}
				<div className={styles.mobilePhoneContainer}>
					<Link href={`tel:${PHONE}`} className={styles.mobilePhoneNumber}>
						+7 (3952) 261-260
					</Link>
				</div>

				<button className={styles.mobileProjectButton} onClick={onProjectClick}>
					<span className={styles.mobileButtonText}>Обсудить проект</span>
					<svg className={styles.mobileButtonIcon} viewBox='0 0 16 16' fill='none'>
						<path
							d='M6.33333 3V4.33333H10.7267L3 12.06L3.94 13L11.6667 5.27333V9.66667H13V3H6.33333Z'
							fill='white'
						/>
					</svg>
				</button>

				<div className={styles.mobileSocialIcons}>
					<IconButton Icon={TelegramIcon} variant='circle' size='sm' asChild>
						<LinkComponent href={socialLinks.telegram} target='_blank' rel='noopener noreferrer' />
					</IconButton>
					<IconButton Icon={VkIcon} variant='circle' size='sm' asChild>
						<LinkComponent href={socialLinks.vk} target='_blank' rel='noopener noreferrer' />
					</IconButton>
					<IconButton Icon={BehanceIcon} variant='circle' size='sm' asChild>
						<LinkComponent href={socialLinks.behance} target='_blank' rel='noopener noreferrer' />
					</IconButton>
					<IconButton Icon={DzenIcon} variant='circle' size='sm' asChild>
						<LinkComponent href={socialLinks.dzen} target='_blank' rel='noopener noreferrer' />
					</IconButton>
					<IconButton variant='circle' size='sm' asChild>
						<LinkComponent href={socialLinks.vc} target='_blank' rel='noopener noreferrer'>
							<svg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
								<g clipPath='url(#clip0_2115_2862_mobile)'>
									<path
										d='M9.58236 5.57127C10.4603 5.57127 11.1212 5.20746 11.6086 4.57409L10.9572 4.07978C10.7964 4.29069 10.5884 4.46155 10.3498 4.57889C10.1113 4.69624 9.84852 4.75687 9.58236 4.75599C8.62956 4.75599 7.93914 3.98388 7.93914 3.03646C7.93914 2.08903 8.61829 1.27343 9.60953 1.27343C10.0502 1.27343 10.3616 1.3769 10.6332 1.54431V2.37738H11.4529V1.16271L11.2958 1.03978C10.8453 0.687178 10.1572 0.5 9.58203 0.5C8.17601 0.5 7.03238 1.63757 7.03238 3.03613C7.03238 4.43338 8.17568 5.57127 9.58236 5.57127ZM11.2829 10.5737L11.2538 9.40611V6.55692H9.54427V7.37253H10.3904V9.3056C10.3904 10.1383 9.75099 10.7279 9.10596 10.7279C8.46093 10.7279 8.12433 10.283 8.12433 9.4506V6.55659H6.52748V7.3722H7.26097V9.59197C7.26097 10.7216 7.89772 11.5 9.03341 11.5C9.68606 11.5 10.183 11.1398 10.4795 10.5845H10.4928L10.6097 11.3609H11.9233V10.5747H11.2839V10.573L11.2829 10.5737ZM6.84354 0.619625H5.41897L4.17992 4.19841H4.1607L2.92828 0.619625H1.41028V1.43556H2.26403L3.73333 5.51459H4.51387V5.5136L6.04611 1.43589H6.84288V0.619957L6.84354 0.619625ZM0.0771484 11.3715H0.921291V10.5321H0.0774797V11.3715H0.0771484ZM3.3371 7.5027H3.32783L3.27482 6.55692H1.76808V7.30036H2.56982V10.574H1.76808V11.3606H5.49185V10.5895H3.4186V8.6139C3.51236 7.8988 4.05138 7.28817 4.78685 7.28817H4.80011L4.7852 8.01909H5.60482V6.5434H4.95681C4.21471 6.54143 3.66409 6.94083 3.33677 7.50303L3.3371 7.5027Z'
										fill='white'
									/>
								</g>
								<defs>
									<clipPath id='clip0_2115_2862_mobile'>
										<rect width='12' height='12' fill='white' />
									</clipPath>
								</defs>
							</svg>
						</LinkComponent>
					</IconButton>
				</div>

				<div className={styles.mobileInfoCompany}>
					<span className={styles.mobileInfoCompanyText}>ООО &quot;Верный код&quot;</span>
					<span className={styles.mobileInfoCompanyText}>ИНН 3811076486</span>
					<span className={styles.mobileInfoCompanyText}>ОКВЭД 62.01 (основной)</span>
					<span className={styles.mobileInfoCompanyText}>
						Код IT-деятельности №1.01 по перечню из Приказа Минцифры № 449
					</span>
				</div>

				<Link href='/privacy.pdf' target='_blank' rel='noopener noreferrer' className={styles.mobilePrivacyLink}>
					<span className={styles.mobilePrivacyText}>Политика конфиденциальности</span>
				</Link>

				<div className={styles.mobileLegalContainer}>
					<div className={styles.mobileCopyright}>© 2026, true.code</div>
				</div>
			</div>
		</footer>
	)
}

Footer.displayName = 'Footer'

export default Footer
